import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useMediaQuery,
  useTheme,
  InputBase,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard,
  Work,
  Notifications,
  Chat,
  Settings,
  Analytics,
  Logout,
  Person,
  Search,
} from "@mui/icons-material";
import apiEndpoints from "../../apiconfig";

const Layout = ({ children, showSnackbar }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const drawerWidth = 280;
  const navigate = useNavigate();
  const location = useLocation();

  // State for counts
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // 🔥 New states for profile image
  const [profileImage, setProfileImage] = useState(null);
  const [userName, setUserName] = useState("User");

  const resolveImageUrl = (imagePath) => {
    if (!imagePath || typeof imagePath !== "string") return null;

    const trimmedPath = imagePath.trim();
    if (
      trimmedPath.startsWith("http://") ||
      trimmedPath.startsWith("https://") ||
      trimmedPath.startsWith("data:") ||
      trimmedPath.startsWith("blob:")
    ) {
      return trimmedPath;
    }

    return `${apiEndpoints.blob}${trimmedPath.replace(/^\/+/, "")}`;
  };

  // Hardcoded user data (kept)
  const layoutData = {
    user: {
      name: "John Doe",
      email: "john@company.com",
      // avatar:
      // "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=150",
    },
    menuItems: [
      { text: "Dashboard", icon: <Dashboard />, path: "/dashboard" },
      { text: "Create Job", icon: <Work />, path: "/create-job" },
      {
        text: "Notifications",
        icon: <Notifications />,
        path: "/notifications",
      },
      { text: "Messages", icon: <Chat />, path: "/chat" },
      { text: "Analytics", icon: <Analytics />, path: "/analysis" },
      { text: "Settings", icon: <Settings />, path: "/settings" },
    ],
  };

  // 🔥 Fetch user profile image
  useEffect(() => {
    const fetchProfileImage = async () => {
      try {
        const cachedProfileImage = sessionStorage.getItem("profileimg_url");
        const cachedUserName = sessionStorage.getItem("userName");
        if (cachedProfileImage) {
          setProfileImage(cachedProfileImage);
        }
        if (cachedUserName) {
          setUserName(cachedUserName);
        }

        const token = sessionStorage.getItem("token");
        if (!token) return;

        const response = await fetch(apiEndpoints.profile, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) throw new Error("Failed to fetch profile");

        const data = await response.json();
        if (data.success && data.data) {
          setProfileImage(data.data.profileimg_url || null);
          setUserName(data.data.userName || "User");
          if (data.data.profileimg_url) {
            sessionStorage.setItem("profileimg_url", data.data.profileimg_url);
          }
          if (data.data.userName) {
            sessionStorage.setItem("userName", data.data.userName);
          }
        }
      } catch (error) {
        console.error("Error fetching profile image:", error);
      }
    };

    fetchProfileImage();
  }, []);

  useEffect(() => {
    const handleProfileImageUpdate = (event) => {
      const nextImage = event?.detail?.profileimg_url;
      const nextName = event?.detail?.userName;

      if (nextImage) {
        setProfileImage(nextImage);
      }
      if (nextName) {
        setUserName(nextName);
      }
    };

    const handleStorage = (event) => {
      if (event.key === "profileimg_url") {
        setProfileImage(event.newValue || null);
      }
      if (event.key === "userName") {
        setUserName(event.newValue || "User");
      }
    };

    window.addEventListener("profile-image-updated", handleProfileImageUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("profile-image-updated", handleProfileImageUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  // Fetch counts
  useEffect(() => {
    fetchCounts();
    const intervalId = setInterval(fetchCounts, 30000);
    return () => clearInterval(intervalId);
  }, [location.pathname]);

  const fetchCounts = async () => {
    try {
      await Promise.all([fetchUnreadMessagesCount(), fetchNotificationCount()]);
    } catch (err) {
      console.error("Error fetching counts:", err);
    }
  };

  const fetchUnreadMessagesCount = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${apiEndpoints.send_message}?action=getUnreadCount`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUnreadMessagesCount(data.count || 0);
      }
    } catch (err) {
      console.error("Error fetching unread messages count:", err);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${apiEndpoints.notification}?action=getcount`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setNotificationCount(data.unread_count || 0);
      } else {
        console.error("Failed to fetch notification count");
      }
    } catch (err) {
      console.error("Error fetching notification count:", err);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    showSnackbar("Logged out successfully", "success");
    navigate("/login");
  };

  const handleNavigation = (path) => {
    if (path === "/notifications") {
      setNotificationCount(0);
    }
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        backgroundColor: "#36656B", // Dark Teal sidebar
        display: "flex",
        flexDirection: "column",
        borderRight: "none",
      }}
    >
      <Box
        sx={{
          p: "20px 16px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            borderRadius: "8px",
          },
        }}
        onClick={() => navigate("/dashboard")}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFFFFF"
          }}
        >
          <svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="6" r="2.5" fill="currentColor" />
            <circle cx="7" cy="22" r="2.5" fill="currentColor" />
            <circle cx="25" cy="22" r="2.5" fill="currentColor" />
            <path d="M11 7C8.5 9 7 12 7 15.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M25 15.5C25 12 23.5 9 21 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M11 25.5C13 26.5 14.5 27 16 27C17.5 27 19 26.5 21 25.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#FFFFFF",
            fontSize: "22px",
            letterSpacing: "-0.8px",
            fontFamily: "'Montserrat', sans-serif",
            textTransform: "lowercase"
          }}
        >
          workroa
        </Typography>
      </Box>

      <List sx={{ px: 2, py: 2, flexGrow: 1 }}>
        {layoutData.menuItems.map((item) => {
          const normalizedPath = (location.pathname || "/").replace(/\/+$/, "") || "/";
          const normalizedItemPath = (item.path || "/").replace(/\/+$/, "") || "/";
          const isActive =
            normalizedPath === normalizedItemPath ||
            (normalizedItemPath !== "/" && normalizedPath.startsWith(`${normalizedItemPath}/`));

          return (
          <ListItem
            button
            key={item.text}
            onClick={() => handleNavigation(item.path)}
            selected={isActive}
            sx={{
              position: "relative",
              borderRadius: "10px",
              mb: 0.5,
              py: 1.5,
              px: 1.5,
              color: "#FFFFFF", // Forced pure white
              transition: "all 0.22s ease",
              overflow: "hidden",
              backgroundColor: isActive ? "rgba(255, 255, 255, 0.22)" : "transparent",
              boxShadow: isActive
                ? "inset 0 0 0 1px rgba(255,255,255,0.16), 0 8px 16px rgba(0,0,0,0.12)"
                : "none",
              "& .MuiListItemIcon-root, & .MuiTypography-root": {
                color: "#FFFFFF !important",
              },
              "&::before": isActive
                ? {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(90deg, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0.10) 65%, rgba(255,255,255,0.04) 100%)",
                    pointerEvents: "none",
                  }
                : {},
              "&::after": isActive
                ? {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 8,
                    bottom: 8,
                    width: 4,
                    borderRadius: "0 6px 6px 0",
                    background: "#F8FAFC",
                    boxShadow: "0 0 10px rgba(255,255,255,0.45)",
                  }
                : {},
              "&:hover": {
                backgroundColor: isActive
                  ? "rgba(255, 255, 255, 0.26)"
                  : "rgba(255, 255, 255, 0.12)", // Critical Step: Hover State
                color: "#FFFFFF !important",
                "& .MuiListItemIcon-root, & .MuiTypography-root": {
                  color: "#FFFFFF !important",
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                color: "inherit",
              }}
            >
              {item.path === "/notifications" ? (
                <Badge badgeContent={notificationCount} color="error" showZero>
                  {item.icon}
                </Badge>
              ) : item.path === "/chat" ? (
                <Badge badgeContent={unreadMessagesCount} color="error" showZero>
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: isActive ? 700 : 500,
                fontSize: "14px",
                color: "#FFFFFF !important", // Forced visibility
                fontFamily: "'Montserrat', sans-serif",
              }}
            />
          </ListItem>
        )})}
      </List>

      <Box p={2} borderTop="1px solid rgba(255, 255, 255, 0.1)">
        <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.5)", display: "block", textAlign: "center" }}>
          © 2024 workroa
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #E2E8F0",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
          width: { lg: `calc(100% - ${drawerWidth}px)` },
          ml: { lg: `${drawerWidth}px` },
          height: 64,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", height: 64 }}>
          <Box display="flex" alignItems="center">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { lg: "none" }, color: "text.primary" }}
            >
              <MenuIcon />
            </IconButton>

            {/* Search Bar - Modern flat style */}
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', backgroundColor: '#F8FAFC', borderRadius: '8px', px: 2, py: 0.5, ml: { xs: 0, sm: 1, md: 2 }, border: '1px solid #E2E8F0', maxWidth: { sm: 210, md: 320 } }}>
              <Search sx={{ color: '#94A3B8', mr: 1 }} />
              <InputBase
                placeholder="Search jobs..."
                sx={{ color: '#0F172A', fontSize: '0.9rem', width: { sm: '140px', md: '250px' } }}
              />
            </Box>
            <Box
              onClick={() => navigate("/dashboard")}
              sx={{
                display: { xs: "flex", lg: "none" },
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
                textDecoration: "none",
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#0F172A"
                }}
              >
                <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="6" r="2.5" fill="currentColor" />
                  <circle cx="7" cy="22" r="2.5" fill="currentColor" />
                  <circle cx="25" cy="22" r="2.5" fill="currentColor" />
                  <path d="M11 7C8.5 9 7 12 7 15.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  <path d="M25 15.5C25 12 23.5 9 21 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  <path d="M11 25.5C13 26.5 14.5 27 16 27C17.5 27 19 26.5 21 25.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#0F172A",
                  fontSize: "20px",
                  letterSpacing: "-0.8px",
                  textTransform: "lowercase"
                }}
              >
                workroa
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              onClick={() => {
                setNotificationCount(0);
                navigate("/notifications");
              }}
              sx={{ color: "text.secondary", "&:hover": { backgroundColor: "#F1F5F9" } }}
            >
              <Badge badgeContent={notificationCount} color="error" showZero>
                <Notifications />
              </Badge>
            </IconButton>

            <IconButton
              onClick={() => navigate("/chat")}
              sx={{ color: "text.secondary", "&:hover": { backgroundColor: "#F1F5F9" } }}
            >
              <Badge badgeContent={unreadMessagesCount} color="error" showZero>
                <Chat />
              </Badge>
            </IconButton>

            <IconButton onClick={handleProfileMenuOpen} sx={{ ml: 1 }}>
              <Avatar
                src={
                  profileImage
                    ? resolveImageUrl(profileImage)
                    : layoutData.user.avatar
                }
                alt={userName}
                sx={{
                  width: isMobile ? 32 : 40,
                  height: isMobile ? 32 : 40,
                  border: "2px solid",
                  borderColor: "#E2E8F0",
                }}
              />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        style={{ marginTop: "8px" }}
      >
        <MenuItem
          onClick={() => {
            handleProfileMenuClose();
            navigate("/settings");
          }}
        >
          <Person style={{ marginRight: "12px", color: "#64748b" }} />
          Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <Logout style={{ marginRight: "12px", color: "#64748b" }} />
          Logout
        </MenuItem>
      </Menu>

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        style={{
          width: drawerWidth,
          flexShrink: 0,
        }}
        PaperProps={{
          style: {
            width: drawerWidth,
            border: "none",
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: "64px",
          width: { xs: "100%", lg: `calc(100% - ${drawerWidth}px)` },
          minHeight: "calc(100vh - 64px)",
          backgroundColor: "#F8FAFC", // Modern flat background
          transition: "margin 0.2s ease-in-out",
          overflowX: "hidden",
          "& > *": {
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          },
        }}
      >
        {children}
      </Box>
    </Box >
  );
};

export default Layout;
